/**
 * SiteConfigContext — config pubblica runtime (PL1, cache PL17).
 *
 * Legge GET /api/public/site-config al boot ed espone il flag
 * `prelaunch`. Runtime, non build-time: al lancio si spegne la env sul
 * backend e il frontend lo riflette senza rebuild.
 *
 * PL17 — stale-while-revalidate: il flag viene ricordato in
 * localStorage e usato SUBITO al boot (loading=false), poi la rete
 * conferma o corregge in background. Risultato: niente schermo bianco
 * in attesa del round-trip — la home dipinge al primo frame. Il primo
 * visitatore in assoluto (nessuna cache) aspetta la rete come prima:
 * default prudente prelaunch=false, mai flash di "in preparazione" su
 * un sito già aperto.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/client';
import { initAnalytics } from '../lib/analytics';
import { initMeta } from '../lib/meta';   // MP2: Meta Pixel, solo col consenso marketing

const CACHE_KEY = 'aurya_site_config';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.prelaunch !== 'boolean') return null;
    return parsed;
  } catch { return null; }
}

// RT1 (piano sito-rete) — oltre al flag legacy `prelaunch`, il contesto
// espone `sitePhase` ('network' | 'marketplace'): i blocchi del sito si
// montano in base alla fase, gli URL non cambiano mai.
const SiteConfigContext = createContext({
  prelaunch: false, sitePhase: 'marketplace', loading: true,
});

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(() => {
    const cached = readCache();
    return cached
      ? { prelaunch: cached.prelaunch,
          sitePhase: cached.sitePhase
            || (cached.prelaunch ? 'network' : 'marketplace'),
          leadMagnetUrl: cached.leadMagnetUrl || null,
          loading: false }   // primo frame utile
      : { prelaunch: false, sitePhase: 'marketplace', leadMagnetUrl: null, loading: true };
  });

  useEffect(() => {
    let mounted = true;
    api.get('/public/site-config')
      .then((res) => {
        const prelaunch = Boolean(res.data?.prelaunch);
        const sitePhase = res.data?.site_phase
          || (prelaunch ? 'network' : 'marketplace');
        const leadMagnetUrl = res.data?.lead_magnet_url || null;
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ prelaunch, sitePhase, leadMagnetUrl })); } catch { /* storage pieno/negato */ }
        if (mounted) setConfig({ prelaunch, sitePhase, leadMagnetUrl, loading: false });
        // GA1 — l'analytics parte qui, con l'ID dal backend: consent
        // mode nega tutto finché il banner non riceve un sì.
        initAnalytics(res.data?.ga_measurement_id);
        // MP2 — stesso schema: ID runtime dal backend; senza ID o senza il
        // si' al marketing non parte nessuna richiesta verso Meta.
        initMeta(res.data?.meta_pixel_id);
      })
      .catch(() => { if (mounted) setConfig((c) => ({ ...c, loading: false })); });
    return () => { mounted = false; };
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
